import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Stack, IconButton, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, Paper,
  FormControlLabel, Alert, Snackbar, Skeleton, InputAdornment, Divider,
} from '@mui/material';
import { Add, Edit, People, LocationOn, Phone, Email, Close, FitnessCenter, Upload, PhotoCamera } from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { gymsAPI, uploadAPI } from '../services/api';
import { CURRENCY_CODE, CURRENCY_SYMBOL, formatCurrency } from '../utils/currency';
import { calculateOnboardingPricing, getDefaultOnboardingPricing } from '../utils/onboardingPricing';

export default function Gyms() {
  const navigate = useNavigate();
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const emptyForm = {
    name: '', address: '', city: '', state: '', zipCode: '',
    phone: '', email: '', website: '', description: '',
    primaryColor: '#1A1A1A', logo: '',
    messageQuota: { maxMessages: 100 },
    onboardingPricing: getDefaultOnboardingPricing(),
    pricing: {
      monthly:   { price: '' },
      quarterly: { price: '', discount: '' },
      annual:    { price: '', discount: '' },
    },
    copyExercises: false, copyWorkoutPlans: false, copyMealPlans: false,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadGyms(); }, []);

  const loadGyms = async () => {
    setLoading(true);
    try {
      const response = await gymsAPI.getAll();
      setGyms(response.data.data);
    } catch (error) {
      console.error('Error loading gyms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const response = await uploadAPI.upload(file);
      const logoUrl = response.data.data.url;
      setForm(f => ({ ...f, logo: logoUrl }));
      setLogoPreview(URL.createObjectURL(file));
    } catch {
      setCreateError('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.address || !form.city) {
      setCreateError('Name, address, and city are required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        onboardingPricing: calculateOnboardingPricing(form.onboardingPricing),
      };
      const res = await gymsAPI.create(payload);
      const { copied } = res.data;
      const parts = [];
      if (copied?.exercises)    parts.push(`${copied.exercises} exercises`);
      if (copied?.workoutPlans) parts.push(`${copied.workoutPlans} workout plans`);
      if (copied?.mealPlans)    parts.push(`${copied.mealPlans} meal plans`);
      const msg = parts.length ? `Gym created. Copied: ${parts.join(', ')}` : 'Gym created successfully';
      setSnackbar({ open: true, message: msg });
      setCreateOpen(false);
      setForm(emptyForm);
      setLogoPreview(null);
      loadGyms();
    } catch (e) {
      setCreateError(e.response?.data?.message || 'Failed to create gym');
    } finally {
      setCreating(false);
    }
  };

  const setPricingField = (tier, field, value) =>
    setForm(f => ({ ...f, pricing: { ...f.pricing, [tier]: { ...f.pricing[tier], [field]: value } } }));

  const setOnboardingFeature = (key, changes) => {
    setForm(f => {
      const nextPricing = {
        ...f.onboardingPricing,
        finalMonthlyCostEdited: false,
        features: f.onboardingPricing.features.map(feature =>
          feature.key === key ? { ...feature, ...changes } : feature
        ),
      };

      return { ...f, onboardingPricing: calculateOnboardingPricing(nextPricing) };
    });
  };

  const setOnboardingFinalCost = value => {
    setForm(f => ({
      ...f,
      onboardingPricing: calculateOnboardingPricing({
        ...f.onboardingPricing,
        finalMonthlyCost: value,
        finalMonthlyCostEdited: true,
      }),
    }));
  };

  const onboardingPricing = calculateOnboardingPricing({
    ...form.onboardingPricing,
    currency: CURRENCY_CODE,
  });

  const serverUrl = process.env.REACT_APP_API_URL?.replace('/api', '');

  return (
    <Box>
      <PageHeader
        title="Gyms"
        subtitle="Manage gym locations and facilities"
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        actions={<Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Add Gym</Button>}
      />

      <Grid container spacing={2.5}>
        {loading
          ? [...Array(6)].map((_, i) => (
              <Grid item xs={12} md={6} lg={4} key={i}>
                <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>

                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="60%" height={16} />
                          <Skeleton width="40%" height={12} />
                        </Box>
                      </Stack>
                      <Skeleton width="80%" height={12} />
                      <Skeleton width="50%" height={12} />
                      <Stack direction="row" spacing={1}>
                        <Skeleton width={80} height={20} sx={{ borderRadius: 10 }} />
                        <Skeleton width={60} height={20} sx={{ borderRadius: 10 }} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))
          : gyms.map((gym) => (
              <Grid item xs={12} md={6} lg={4} key={gym._id}>
                <Card elevation={0} sx={{
                  border: 1, borderColor: 'divider', borderRadius: 2,
                  cursor: 'pointer', transition: 'all 0.2s ease',
                  minHeight: 200,
                  '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
                }} onClick={() => navigate(`/dashboard/gyms/${gym._id}`)}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={gym.logo ? (gym.logo.startsWith('http') ? gym.logo : `${serverUrl}${gym.logo}`) : undefined}
                            variant="rounded"
                            sx={{ bgcolor: 'primary.main', width: 40, height: 40, borderRadius: 1.5 }}
                          >
                            <FitnessCenter sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.9375rem', lineHeight: 1.3 }}>
                              {gym.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{gym.city}</Typography>
                          </Box>
                        </Stack>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/dashboard/gyms/${gym._id}`); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <LocationOn sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary" noWrap>{gym.address}</Typography>
                        </Stack>
                        {gym.phone && (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Phone sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">{gym.phone}</Typography>
                          </Stack>
                        )}
                        {gym.email && (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Email sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary" noWrap>{gym.email}</Typography>
                          </Stack>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Chip icon={<People sx={{ fontSize: 13 }} />}
                          label={`${gym.activeMembers || 0} Active`} size="small" color="success"
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                        <Chip label={`${gym.totalMembers || 0} Total`} size="small"
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
      </Grid>

      {!loading && gyms.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <FitnessCenter sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>No gyms yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 320, mx: 'auto' }}>
            Add your first gym location to get started.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
            sx={{ minWidth: 160 }}>
            Add First Gym
          </Button>
        </Box>
      )}

      {/* ── Create Gym Dialog ─────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Create New Gym</Typography>
            <IconButton onClick={() => setCreateOpen(false)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {createError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>{createError}</Alert>}

          {/* Logo */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Logo
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={logoPreview || undefined} variant="rounded"
                sx={{ width: 72, height: 72, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}>
                {!logoPreview && <PhotoCamera sx={{ fontSize: 28, color: 'text.disabled' }} />}
              </Avatar>
              <Button variant="outlined" component="label" size="small"
                startIcon={<Upload />} disabled={logoUploading}>
                {logoUploading ? 'Uploading…' : 'Upload Logo'}
                <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
              </Button>
            </Stack>
          </Paper>

          {/* Basic Info */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Basic Info
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Gym Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="City *" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Zip Code" value={form.zipCode} onChange={e => setForm(f => ({ ...f, zipCode: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Address *" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth size="small" multiline rows={2} />
              </Grid>
            </Grid>
          </Paper>

          {/* Contact */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} fullWidth size="small" />
              </Grid>
            </Grid>
          </Paper>

          {/* Platform Pricing Model */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                  Platform Pricing Model
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Service bundles are selected by default. Adjust bundle prices or override the final monthly cost.
                </Typography>
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, minWidth: { xs: '100%', md: 220 }, bgcolor: 'background.paper' }}>
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Features</Typography>
                    <Typography variant="caption" fontWeight={700}>{formatCurrency(onboardingPricing.monthlyFeatureTotal)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Maintenance & record keeping</Typography>
                    <Typography variant="caption" fontWeight={700}>{formatCurrency(onboardingPricing.maintenanceCharge)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={700}>Final Monthly Cost</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary.main">{formatCurrency(onboardingPricing.finalMonthlyCost)}</Typography>
                  </Stack>
                  <TextField
                    label="Editable Final Cost"
                    type="number"
                    size="small"
                    value={onboardingPricing.finalMonthlyCost}
                    onChange={e => setOnboardingFinalCost(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                  />
                </Stack>
              </Paper>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1.5}>
              {onboardingPricing.features.map(feature => (
                <Grid item xs={12} md={6} key={feature.key}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ p: 1.5, border: 1, borderColor: feature.selected ? 'primary.main' : 'divider', borderRadius: 1.5 }}>
                    <FormControlLabel
                      sx={{ flex: 1, mr: 0 }}
                      control={
                        <Checkbox
                          checked={feature.selected}
                          onChange={e => setOnboardingFeature(feature.key, { selected: e.target.checked })}
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>{feature.label}</Typography>}
                    />
                    <TextField
                      label="Monthly Cost"
                      type="number"
                      size="small"
                      value={feature.price}
                      onChange={e => setOnboardingFeature(feature.key, { price: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                      sx={{ width: { xs: '100%', sm: 150 } }}
                    />
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Pricing */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Membership Pricing
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="Monthly Price" type="number" size="small" fullWidth
                  value={form.pricing.monthly.price}
                  onChange={e => setPricingField('monthly', 'price', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <TextField label="Quarterly Price" type="number" size="small" fullWidth
                    value={form.pricing.quarterly.price}
                    onChange={e => setPricingField('quarterly', 'price', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }} />
                  <TextField label="Quarterly Discount %" type="number" size="small" fullWidth
                    value={form.pricing.quarterly.discount}
                    onChange={e => setPricingField('quarterly', 'discount', e.target.value)} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <TextField label="Annual Price" type="number" size="small" fullWidth
                    value={form.pricing.annual.price}
                    onChange={e => setPricingField('annual', 'price', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }} />
                  <TextField label="Annual Discount %" type="number" size="small" fullWidth
                    value={form.pricing.annual.discount}
                    onChange={e => setPricingField('annual', 'discount', e.target.value)} />
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Copy content */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Copy Content from Default Gym (optional)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <FormControlLabel control={<Checkbox checked={form.copyExercises} onChange={e => setForm(f => ({ ...f, copyExercises: e.target.checked }))} />} label="Exercises" />
              <FormControlLabel control={<Checkbox checked={form.copyWorkoutPlans} onChange={e => setForm(f => ({ ...f, copyWorkoutPlans: e.target.checked }))} />} label="Workout Plans" />
              <FormControlLabel control={<Checkbox checked={form.copyMealPlans} onChange={e => setForm(f => ({ ...f, copyMealPlans: e.target.checked }))} />} label="Meal Plans" />
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setCreateOpen(false); setCreateError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create Gym'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))} message={snackbar.message} />
    </Box>
  );
}
